import {
  useChangeRequestFileUpdate as useChangeRequestFileUpdateREST,
  useFileUpdate as useFileUpdateREST,
  useMultiParentFileUpdate as useMultiParentFileUpdateREST,
} from '@risksmart-app/components/src/file/useFileUpdate';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import {
  useChangeRequestFileUpdateTRPC,
  useFileUpdateTRPC,
  useMultiParentFileUpdateTRPC,
} from './useFileUpdateTRPC';

/**
 * Flag-gated wrappers over the V2 file-update hooks
 * (`@risksmart-app/components/src/file/useFileUpdate` — REST upload +
 * Apollo delete via `DeleteRelationFileByIdDocument`) and their tRPC twins
 * (`files.deleteRelationFiles`). Consumers import THESE — never the
 * `*TRPC` variants directly.
 *
 * Exemption: `src/data/ApolloGraphqlProvider.tsx` stays on the components
 * REST `useChangeRequestFileUpdate` — it renders above `FeatureProvider`
 * (mounted in the layouts), so the flag hook would throw there, and its
 * call site is the Apollo error-link CR replay, which only ever runs on
 * the legacy path anyway (the tRPC CR flow goes through
 * `ChangeRequestConfirmProvider`).
 *
 * Bespoke by decision (RSP-6330): NOT built with `createMutationHook`. The
 * legacy branch lives in the components package (REST upload + Apollo
 * delete), and each update composes an upload with a relation-file delete —
 * the factory models exactly one Apollo document + one tRPC procedure.
 */
export const useFileUpdate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const rest = useFileUpdateREST();
  const trpc = useFileUpdateTRPC();

  return trpcEnabled ? trpc : rest;
};

export const useChangeRequestFileUpdate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const rest = useChangeRequestFileUpdateREST();
  const trpc = useChangeRequestFileUpdateTRPC();

  return trpcEnabled ? trpc : rest;
};

export const useMultiParentFileUpdate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const rest = useMultiParentFileUpdateREST();
  const trpc = useMultiParentFileUpdateTRPC();

  return trpcEnabled ? trpc : rest;
};
